/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Stack } from '@atlaskit/primitives/compiled';
import type { QueryData } from '@atlassian/audit-logs-common';

import Avatar from '../../../common/utils/components/avatar';
import AvatarItem from '../../../common/utils/components/avatar-item';
import { CopyJsonButton } from '../../../common/utils/components/copy-json-button';
import { EventDateTime } from '../../../common/utils/components/event-date-time';
import {
	SummarySection,
	type SummarySectionRow,
} from '../../../common/utils/components/summary-section';

import { FiltersSection } from './filters-section';
import { QueryStatusLozenge } from './query-status-lozenge';

const messages = defineMessages({
	summary: {
		id: 'organization.audit.log.side-panel.tab-panels.query-details.summary-section.title',
		defaultMessage: 'Summary',
		description: 'Title for the summary section in query details panel',
	},
	name: {
		id: 'organization.audit.log.side-panel.tab-panels.query-details.summary-section.name.label',
		defaultMessage: 'Name',
		description: 'Label for the query name field',
	},
	description: {
		id: 'organization.audit.log.side-panel.tab-panels.query-details.summary-section.description.label',
		defaultMessage: 'Description',
		description: 'Label for the query description field',
	},
	status: {
		id: 'organization.audit.log.side-panel.tab-panels.query-details.summary-section.status.label',
		defaultMessage: 'Status',
		description: 'Label for the query status field',
	},
	createdAt: {
		id: 'organization.audit.log.side-panel.tab-panels.query-details.summary-section.created-at.label',
		defaultMessage: 'Created',
		description: 'Label for the query created at field',
	},
	updatedAt: {
		id: 'organization.audit.log.side-panel.tab-panels.query-details.summary-section.updated-at.label',
		defaultMessage: 'Completed',
		description: 'Label for the query completed/updated at field',
	},
	createdBy: {
		id: 'organization.audit.log.side-panel.tab-panels.query-details.summary-section.created-by.label',
		defaultMessage: 'Created By',
		description: 'Label for the query created by field',
	},
	id: {
		id: 'organization.audit.log.side-panel.tab-panels.query-details.summary-section.id.label',
		defaultMessage: 'Query ID',
		description: 'Label for the query ID field in query details panel',
	},
	json: {
		id: 'organization.audit.log.side-panel.tab-panels.query-details.summary-section.json.label',
		defaultMessage: 'JSON',
		description: 'Label for the JSON data field in query details panel',
	},
});

interface QueryDetailsPanelProps {
	queryData: QueryData;
}

export const QueryDetailsPanel = ({ queryData }: QueryDetailsPanelProps): JSX.Element | null => {
	const { formatMessage } = useIntl();

	if (!queryData) {
		return null;
	}

	const { name, description, status, createdAt, updatedAt, createdBy, id } = queryData;

	const rows: SummarySectionRow[] = [
		{ label: formatMessage(messages.name), value: name },
		{ label: formatMessage(messages.description), value: description },
		{ label: formatMessage(messages.id), value: id },
		{ label: formatMessage(messages.status), value: <QueryStatusLozenge status={status} /> },
		{ label: formatMessage(messages.createdAt), value: <EventDateTime timestamp={createdAt} /> },
		...(status === 'COMPLETED' && updatedAt
			? [
					{
						label: formatMessage(messages.updatedAt),
						value: <EventDateTime timestamp={updatedAt} />,
					},
				]
			: []),
		...(createdBy?.name
			? [
					{
						label: formatMessage(messages.createdBy),
						value: (
							<AvatarItem
								primaryText={createdBy.name}
								avatar={<Avatar size="small" src={createdBy.avatarUrl} />}
							/>
						),
					},
				]
			: []),
		{
			label: formatMessage(messages.json),
			value: <CopyJsonButton eventJSON={JSON.stringify(queryData)} />,
		},
	];

	return (
		<Stack space="space.300">
			<SummarySection title={formatMessage(messages.summary)} rows={rows} />
			<FiltersSection filters={queryData.filters} />
		</Stack>
	);
};
